/**
 * The eight predicates, each with the share of the book's own writing it calls clean.
 *
 * The rates are not a scoreboard for the rules — a low one means the rule and the book
 * disagree often, which is a finding about the encoding as much as about the music. What the
 * page has to make unmissable is the averaging: eight votes, one each, and a rule that judged
 * nothing gets no vote rather than a free one.
 */

import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import { Failed, Kicker, Loading, Note } from "../components/Page";
import { count, percent, rate } from "../lib/format";

export default function RulesPage(): JSX.Element {
  const { data, error, loading } = useAsync("rules", () => api.rules());

  return (
    <Board
      kicker="Eight predicates"
      title="The rules"
      lede="Each one is a sentence, a table or a figure from Principles of Orchestration turned into a predicate that runs both ways: forwards it judges, backwards it voices."
    >
      {loading ? (
        <Loading what="the rules" />
      ) : error !== null ? (
        <Failed error={error} />
      ) : data === null ? (
        <Failed error="the rules came back empty" />
      ) : (
        <div className="space-y-6">
          {data.overall === null ? (
            <Note>{data.note}</Note>
          ) : (
            <p className="text-sm text-ink-faded">
              Averaged one vote each, the eight score{" "}
              <span className="font-mono text-ink">{rate(data.overall)}</span> over the book.
            </p>
          )}

          <ul className="space-y-2">
            {data.rules.map((one) => (
              <li key={one.predicate}>
                <Link
                  to={`/rules/${one.predicate}`}
                  className="block rounded-xl border border-rule bg-leaf p-4 hover:border-ink-faded"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-ink-quiet">
                      {one.position}/{one.of}
                    </span>
                    <span className="flex-1 text-[15px] font-semibold">{one.predicate}</span>
                    <span className="font-mono text-[15px]">{rate(one.total?.score)}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-faded">{one.statement}</p>
                  <div className="mt-3 h-[5px] overflow-hidden rounded-[3px] bg-rule-soft">
                    {one.total?.score === undefined || one.total?.score === null ? null : (
                      <span
                        className={`block h-[5px] ${percent(one.total.score) >= 90 ? "bg-clean" : "bg-fault"}`}
                        style={{ width: `${percent(one.total.score)}%` }}
                      />
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-ink-quiet">
                    <span>{one.source}</span>
                    {one.total ? (
                      <span>
                        {count(one.total.judged)} judged · {count(one.total.spokeIn)} of 314 scores
                      </span>
                    ) : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <section className="rounded-xl border border-rule bg-leaf p-4">
            <Kicker>Silence is not a pass</Kicker>
            <p className="mt-2 text-sm leading-relaxed text-ink-faded">
              A predicate that had nothing to judge returns nothing at all, and the eight are
              averaged one vote each — so a rule cannot buy the score by declining to speak.
            </p>
          </section>
        </div>
      )}
    </Board>
  );
}
